import React, { useState, useCallback, useEffect, useMemo, useContext, useRef } from 'react';
import { AuthContext } from './authContextDef';
import { PremiumContext, type PremiumContextType } from './premiumContextDef';
import {
  fetchPremiumData,
  subscribeToPremiumStatus,
  getCustomerPortalUrl,
  readCachedPremium,
  writeCachedPremium,
} from '../services/lemonsqueezy';

import type { PremiumData } from '../types';

// Re-export types and context
export { PremiumContext, type PremiumContextType } from './premiumContextDef';

/**
 * PremiumProvider manages the user's premium subscription state via Firestore.
 *
 * Premium status is written to Firestore by the Lemon Squeezy webhook handler
 * and read here in real-time via a Firestore snapshot listener.
 *
 * Premium status sources (in order of priority):
 * 1. Firestore real-time listener (authoritative, updated by webhook)
 * 2. localStorage cache (offline fallback)
 * 3. Default: free tier
 *
 * Purchase flow:
 * 1. Paywall opens a Lemon Squeezy checkout URL
 * 2. User completes payment on Lemon Squeezy
 * 3. Webhook writes premium status to Firestore
 * 4. Firestore listener picks up the change and updates context
 */
export const PremiumProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authCtx = useContext(AuthContext);
  const cachedData = readCachedPremium();
  const [isPremium, setIsPremium] = useState(cachedData?.isPremium ?? false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaywallVisible, setIsPaywallVisible] = useState(false);
  const [managementURL, setManagementURL] = useState<string | null>(null);

  const isMountedRef = useRef(true);
  const prevUidRef = useRef<string | null | undefined>(undefined);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    prevUidRef.current = undefined;
    return () => {
      isMountedRef.current = false;
      // Clean up Firestore listener
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, []);

  /**
   * Apply premium data to local state and cache.
   */
  const applyPremiumData = useCallback((data: PremiumData | null) => {
    if (!isMountedRef.current) return;

    const premium = data?.isPremium ?? false;
    setIsPremium(premium);
    writeCachedPremium(data ?? { isPremium: false });
  }, []);

  /**
   * Reset state to free tier (no user logged in).
   */
  const resetToFreeTier = useCallback(() => {
    if (!isMountedRef.current) return;
    setIsPremium(false);
    setManagementURL(null);
    setIsLoading(false);
  }, []);

  /**
   * Refresh premium status by re-fetching from Firestore.
   */
  const refreshPremiumStatus = useCallback(async () => {
    const uid = authCtx?.uid;
    if (!uid) return;

    try {
      const data = await fetchPremiumData(uid);
      applyPremiumData(data);

      // Also refresh management URL
      const portalUrl = await getCustomerPortalUrl(uid);
      if (isMountedRef.current) {
        setManagementURL(portalUrl);
      }
    } catch (_error) {
      // Firestore unavailable — fall back to cache
      if (isMountedRef.current) {
        const cached = readCachedPremium();
        setIsPremium(cached?.isPremium ?? false);
      }
    }
  }, [authCtx?.uid, applyPremiumData]);

  // Set up Firestore real-time listener when auth state is ready
  useEffect(() => {
    // If auth is still loading, wait
    if (authCtx?.isLoading) return;

    const uid = authCtx?.uid ?? null;

    // Skip if UID hasn't changed (avoid re-running on unrelated re-renders)
    if (prevUidRef.current === uid) return;
    prevUidRef.current = uid;

    // Clean up previous listener
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    if (!uid) {
      // No user — reset to free tier (deferred to avoid synchronous setState in effect)
      queueMicrotask(() => resetToFreeTier());
      return;
    }

    // Subscribe to real-time premium status updates from Firestore
    let isFirstCallback = true;
    const unsubscribe = subscribeToPremiumStatus(uid, (data) => {
      applyPremiumData(data);

      if (isFirstCallback) {
        isFirstCallback = false;
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }

      // If premium, auto-close paywall
      if (data?.isPremium && isMountedRef.current) {
        setIsPaywallVisible(false);
      }
    });
    unsubscribeRef.current = unsubscribe;

    // Also fetch management URL
    getCustomerPortalUrl(uid).then((url) => {
      if (isMountedRef.current) {
        setManagementURL(url);
      }
    }).catch(() => {
      // Ignore — management URL is best-effort
    });

    // If Firestore listener didn't fire within 3s, stop loading
    const timeout = setTimeout(() => {
      if (isFirstCallback && isMountedRef.current) {
        isFirstCallback = false;
        setIsLoading(false);
      }
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [authCtx?.isLoading, authCtx?.uid, applyPremiumData, resetToFreeTier]);

  const showPaywall = useCallback(() => {
    setIsPaywallVisible(true);
  }, []);

  const hidePaywall = useCallback(() => {
    setIsPaywallVisible(false);
  }, []);

  /**
   * Restore a previous purchase by re-checking Firestore.
   * With Lemon Squeezy, purchases are associated via Firebase UID in custom data,
   * so if the webhook has already written the premium status, this will find it.
   */
  const restorePurchase = useCallback(async () => {
    await refreshPremiumStatus();
  }, [refreshPremiumStatus]);

  const value: PremiumContextType = useMemo(
    () => ({
      isPremium,
      isLoading,
      showPaywall,
      hidePaywall,
      isPaywallVisible,
      refreshPremiumStatus,
      restorePurchase,
      managementURL,
    }),
    [isPremium, isLoading, showPaywall, hidePaywall, isPaywallVisible, refreshPremiumStatus, restorePurchase, managementURL],
  );

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
};
